var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { cn } from '../../lib/utils.js';
import { Button } from '../ui/button.js';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover.js';
import { format, isSameMonth, isAfter, isBefore, parse, isValid, } from 'date-fns';
export function MonthPickerRange(_a) {
    var initialStartDate = _a.initialStartDate, initialEndDate = _a.initialEndDate, onApply = _a.onApply, className = _a.className, _b = _a.placeholder, placeholder = _b === void 0 ? 'Select month range' : _b, _c = _a.disabled, disabled = _c === void 0 ? false : _c, minDate = _a.minDate, maxDate = _a.maxDate, _d = _a.disabledFuture, disabledFuture = _d === void 0 ? true : _d;
    // Calculate default range (last 30 days)
    var getDefaultRange = function () {
        var end = new Date();
        var start = new Date();
        start.setDate(start.getDate() - 30);
        return { start: start, end: end };
    };
    // Initialize with provided dates or default range
    var getInitialRange = function () {
        if (initialStartDate && initialEndDate) {
            return { start: initialStartDate, end: initialEndDate };
        }
        return getDefaultRange();
    };
    var _e = React.useState(false), open = _e[0], setOpen = _e[1];
    var _f = React.useState(getInitialRange()), tempRange = _f[0], setTempRange = _f[1];
    var _g = React.useState(false), selectingEnd = _g[0], setSelectingEnd = _g[1];
    var _h = React.useState(function () {
        var range = getInitialRange();
        return range.start ? range.start.getFullYear() : new Date().getFullYear();
    }), startYear = _h[0], setStartYear = _h[1];
    var _j = React.useState(function () {
        var range = getInitialRange();
        var startYear = range.start ? range.start.getFullYear() : new Date().getFullYear();
        var endYear = range.end ? range.end.getFullYear() : startYear;
        // Ensure endYear is always at least startYear + 1 (never same year)
        return endYear > startYear ? endYear : startYear + 1;
    }), endYear = _j[0], setEndYear = _j[1];
    var _k = React.useState(null), hoverMonth = _k[0], setHoverMonth = _k[1];
    var _l = React.useState(function () {
        var range = getInitialRange();
        return range.start ? format(range.start, 'MMM yyyy') : '';
    }), startInput = _l[0], setStartInput = _l[1];
    var _m = React.useState(function () {
        var range = getInitialRange();
        return range.end ? format(range.end, 'MMM yyyy') : '';
    }), endInput = _m[0], setEndInput = _m[1];
    var months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
    ];
    // Helper function to check if a month is disabled based on min/max dates
    var isMonthDisabled = function (year, monthIndex) {
        var monthStart = new Date(year, monthIndex, 1);
        var monthEnd = new Date(year, monthIndex + 1, 0); // last day of month
        // Check if month is outside min/max date range
        var isBeforeMin = minDate && monthEnd < minDate;
        var isAfterMax = maxDate && monthStart > maxDate;
        return isBeforeMin || isAfterMax;
    };
    // Helper functions for year navigation with validation
    var handleStartYearPrevious = function () {
        setStartYear(function (prev) { return prev - 1; });
    };
    var handleStartYearNext = function () {
        // Only allow if it won't overlap with end year
        if (startYear + 1 < endYear) {
            setStartYear(function (prev) { return prev + 1; });
        }
    };
    var handleEndYearPrevious = function () {
        // Only allow if it won't overlap with start year
        if (endYear - 1 > startYear) {
            setEndYear(function (prev) { return prev - 1; });
        }
    };
    var handleEndYearNext = function () {
        setEndYear(function (prev) { return prev + 1; });
    };
    var handleMonthClick = function (year, monthIndex) {
        // Check if month is disabled due to min/max date constraints
        if (isMonthDisabled(year, monthIndex)) {
            return; // Don't allow selection of months outside the allowed range
        }
        var selectedMonthStart = new Date(year, monthIndex, 1);
        var selectedMonthEnd = new Date(year, monthIndex + 1, 0); // last day of month (handles leap years)
        // If a complete range is already selected, start over with the new month as start
        if (tempRange.start && tempRange.end && !selectingEnd) {
            var newRange = { start: selectedMonthStart, end: null };
            setTempRange(newRange);
            setStartInput(format(selectedMonthStart, 'MMM yyyy'));
            setEndInput('');
            setSelectingEnd(true);
            return;
        }
        if (!selectingEnd && !tempRange.start) {
            // First click - set start month
            var newRange = { start: selectedMonthStart, end: null };
            setTempRange(newRange);
            setStartInput(format(selectedMonthStart, 'MMM yyyy'));
            setSelectingEnd(true);
        }
        else if (selectingEnd || !tempRange.end) {
            var newRange = void 0;
            if (tempRange.start && isAfter(selectedMonthStart, tempRange.start)) {
                // Valid end month (after start)
                newRange = { start: tempRange.start, end: selectedMonthEnd };
                setStartInput(format(tempRange.start, 'MMM yyyy'));
                setEndInput(format(selectedMonthEnd, 'MMM yyyy'));
            }
            else if (tempRange.start && isBefore(selectedMonthStart, tempRange.start)) {
                // If selected month is before start, swap them
                var swappedEnd = new Date(tempRange.start.getFullYear(), tempRange.start.getMonth() + 1, 0);
                newRange = { start: selectedMonthStart, end: swappedEnd };
                setStartInput(format(selectedMonthStart, 'MMM yyyy'));
                setEndInput(format(swappedEnd, 'MMM yyyy'));
            }
            else {
                // Same month selected
                newRange = { start: selectedMonthStart, end: selectedMonthEnd };
                setStartInput(format(selectedMonthStart, 'MMM yyyy'));
                setEndInput(format(selectedMonthEnd, 'MMM yyyy'));
            }
            setTempRange(newRange);
            setSelectingEnd(false);
        }
    };
    var _o = React.useState(getInitialRange()), selectedRange = _o[0], setSelectedRange = _o[1];
    React.useEffect(function () {
        // Get the new range based on current props
        var newRange = getInitialRange();
        // Update all relevant state
        setSelectedRange(newRange);
        setTempRange(newRange);
        setStartInput(newRange.start ? format(newRange.start, 'MMM yyyy') : '');
        setEndInput(newRange.end ? format(newRange.end, 'MMM yyyy') : '');
        // Update year views if we have dates
        if (newRange.start) {
            var startYear_1 = newRange.start.getFullYear();
            setStartYear(startYear_1);
            if (newRange.end) {
                var endYear_1 = newRange.end.getFullYear();
                // Ensure endYear is always greater than startYear
                setEndYear(endYear_1 > startYear_1 ? endYear_1 : startYear_1 + 1);
            }
            else {
                // If we have a start date but no end date, set endYear to startYear + 1
                setEndYear(startYear_1 + 1);
            }
        }
        // Reset selection state
        setSelectingEnd(false);
        setHoverMonth(null);
    }, [initialStartDate, initialEndDate]);
    var handleApply = function () {
        if (tempRange.start && tempRange.end) {
            setSelectedRange(tempRange);
            onApply === null || onApply === void 0 ? void 0 : onApply(tempRange.start, tempRange.end);
            setOpen(false);
        }
    };
    var handleCancel = function () {
        setOpen(false);
        setTempRange(selectedRange);
        setSelectingEnd(false);
        setHoverMonth(null);
        setStartInput(selectedRange.start ? format(selectedRange.start, 'MMM yyyy') : '');
        setEndInput(selectedRange.end ? format(selectedRange.end, 'MMM yyyy') : '');
    };
    var handleOpenChange = function (newOpen) {
        setOpen(newOpen);
        if (newOpen) {
            setTempRange(selectedRange);
            setSelectingEnd(false);
            setHoverMonth(null);
            if (selectedRange.start) {
                var startYear_2 = selectedRange.start.getFullYear();
                setStartYear(startYear_2);
                if (selectedRange.end) {
                    var endYear_2 = selectedRange.end.getFullYear();
                    // Ensure endYear is always greater than startYear
                    setEndYear(endYear_2 > startYear_2 ? endYear_2 : startYear_2 + 1);
                }
                else {
                    setEndYear(startYear_2 + 1);
                }
            }
            setStartInput(selectedRange.start ? format(selectedRange.start, 'MMM yyyy') : '');
            setEndInput(selectedRange.end ? format(selectedRange.end, 'MMM yyyy') : '');
        }
        else {
            setHoverMonth(null);
        }
    };
    var handleStartInputChange = function (e) {
        setStartInput(e.target.value);
        var parsed = parse(e.target.value, 'MMM yyyy', new Date());
        if (isValid(parsed)) {
            setTempRange(function (prev) { return (__assign(__assign({}, prev), { start: parsed })); });
        }
    };
    var handleEndInputChange = function (e) {
        setEndInput(e.target.value);
        var parsed = parse(e.target.value, 'MMM yyyy', new Date());
        if (isValid(parsed)) {
            var lastDay_1 = new Date(parsed.getFullYear(), parsed.getMonth() + 1, 0); // last day of month (handles leap years)
            setTempRange(function (prev) { return (__assign(__assign({}, prev), { end: lastDay_1 })); });
        }
    };
    var isInRange = function (year, monthIndex) {
        if (!tempRange.start || !tempRange.end)
            return false;
        var monthDate = new Date(year, monthIndex, 1);
        return monthDate >= tempRange.start && monthDate <= tempRange.end;
    };
    var isInHoverRange = function (year, monthIndex) {
        if (!selectingEnd || !tempRange.start || !hoverMonth)
            return false;
        var monthDate = new Date(year, monthIndex, 1);
        var start = tempRange.start;
        var end = hoverMonth;
        if (start <= end) {
            return monthDate >= start && monthDate <= end;
        }
        else {
            return monthDate >= end && monthDate <= start;
        }
    };
    var isStartMonth = function (year, monthIndex) {
        if (!tempRange.start)
            return false;
        var monthDate = new Date(year, monthIndex, 1);
        return isSameMonth(monthDate, tempRange.start);
    };
    var isEndMonth = function (year, monthIndex) {
        if (!tempRange.end)
            return false;
        var monthDate = new Date(year, monthIndex, 1);
        return isSameMonth(monthDate, tempRange.end);
    };
    var getMonthClassName = function (year, monthIndex) {
        var classes = ['month-tile'];
        if (isStartMonth(year, monthIndex)) {
            classes.push('range-start');
        }
        else if (isEndMonth(year, monthIndex)) {
            classes.push('range-end');
        }
        else if (isInRange(year, monthIndex)) {
            classes.push('range-middle');
        }
        else if (isInHoverRange(year, monthIndex)) {
            classes.push('range-hover');
        }
        return classes.join(' ');
    };
    var formatMonthRange = function () {
        if (tempRange.start && tempRange.end) {
            if (isSameMonth(tempRange.start, tempRange.end)) {
                return format(tempRange.start, 'MMM yyyy');
            }
            return "".concat(format(tempRange.start, 'MMM yyyy'), " - ").concat(format(tempRange.end, 'MMM yyyy'));
        }
        else if (tempRange.start) {
            return "".concat(format(tempRange.start, 'MMM yyyy'), " - End Month");
        }
        return placeholder;
    };
    var renderYear = function (year, isStartCalendar) {
        var now = new Date();
        var currentYear = now.getFullYear();
        var currentMonth = now.getMonth();
        // Navigation handlers based on which calendar this is
        var handlePrevious = isStartCalendar ? handleStartYearPrevious : handleEndYearPrevious;
        var handleNext = isStartCalendar ? handleStartYearNext : handleEndYearNext;
        // Disabled logic for arrows - only disable inner arrows that could cause overlap
        var disableLeft = isStartCalendar ? false : endYear - 1 <= startYear;
        var disableRight = isStartCalendar ? startYear + 1 >= endYear : false;
        return (_jsxs("div", { className: "year-panel", children: [_jsx("div", { className: "year-header", children: _jsxs("div", { className: "year-navigation", children: [_jsx("button", { className: "month-picker-nav", onClick: handlePrevious, disabled: disableLeft, children: _jsx(ChevronLeft, { className: "h-5 w-5 text-gray-600 dark:text-muted-foreground" }) }), _jsx("h3", { className: "year-title", children: year }), _jsx("button", { className: "month-picker-nav", onClick: handleNext, disabled: disableRight, children: _jsx(ChevronRight, { className: "h-5 w-5 text-gray-600 dark:text-muted-foreground" }) })] }) }), _jsx("div", { className: "months-grid", children: months.map(function (month, index) {
                        var isFuture = disabledFuture ? year > currentYear || (year === currentYear && index > currentMonth) : false;
                        var isDisabledByDateRange = isMonthDisabled(year, index);
                        var isDisabled = isFuture || isDisabledByDateRange;
                        return (_jsx("button", { onClick: function () {
                                if (!isDisabled)
                                    handleMonthClick(year, index);
                            }, onMouseEnter: function () {
                                if (!isDisabled && selectingEnd && tempRange.start) {
                                    setHoverMonth(new Date(year, index, 1));
                                }
                            }, onMouseLeave: function () {
                                if (!isDisabled && selectingEnd) {
                                    setHoverMonth(null);
                                }
                            }, className: getMonthClassName(year, index) + (isDisabled ? ' disabled' : ''), disabled: isDisabled, "aria-disabled": isDisabled, children: month }, "".concat(year, "-").concat(index)));
                    }) })] }, year));
    };
    return (_jsxs(_Fragment, { children: [_jsx("style", { children: "\n        .month-picker-container {\n          background: white;\n          border: none;\n          width: 100%;\n          max-width: 720px;\n        }\n        .dark .month-picker-container {\n          background: hsl(var(--background));\n        }\n        \n        .month-picker-header {\n          display: flex;\n          justify-content: space-between;\n          align-items: center;\n          margin-bottom: 0.75rem;\n          padding: 0 1rem;\n          height: 44px;\n        }\n        \n        .month-picker-nav {\n          background: none;\n          border: none;\n          cursor: pointer;\n          padding: 4px;\n          border-radius: 6px;\n          display: flex;\n          align-items: center;\n          justify-content: center;\n          width: 32px;\n          height: 25.33px;\n        }\n        \n        .month-picker-nav:hover {\n          background-color: #f5f5f5;\n        }\n        .dark .month-picker-nav:hover {\n          background-color: hsl(var(--muted));\n        }\n        \n        .month-picker-nav:disabled {\n          cursor: not-allowed;\n          opacity: 0.5;\n          background-color: transparent;\n        }\n        \n        .month-picker-nav:disabled:hover {\n          background-color: transparent;\n        }\n        \n        .month-picker-content {\n          display: flex;\n          gap: 2.5rem;\n        }\n        \n        .year-panel {\n          flex: 1;\n          min-width: 180px;\n          display: flex;\n          flex-direction: column;\n          align-items: center;\n        }\n        \n        .year-header {\n          text-align: center;\n          margin-bottom: 0.5rem;\n        }\n        \n        .year-navigation {\n          display: flex;\n          align-items: center;\n          justify-content: center;\n          gap: 0.5rem;\n        }\n        \n        .year-title {\n          font-size: 1rem;\n          font-weight: 600;\n          color: #000000;\n          margin: 0;\n          min-width: 4rem;\n        }\n        .dark .year-title {\n          color: hsl(var(--foreground));\n        }\n        \n        .months-grid {\n          display: grid;\n          grid-template-columns: repeat(3, 1fr);\n          gap: 6px;\n          width: 180px;\n        }\n        \n        .month-tile {\n          background: none;\n          border: none;\n          cursor: pointer;\n          font-size: 0.8125rem;\n          height: 32px;\n          width: 52px;\n          display: flex;\n          align-items: center;\n          justify-content: center;\n          border-radius: 4px;\n          transition: all 0.15s ease;\n          color: #333333;\n          font-weight: 400;\n          padding: 0 6px;\n        }\n        .dark .month-tile {\n          color: hsl(var(--foreground));\n        }\n        \n        .month-tile:hover {\n          background-color: #F4FFC6 !important;\n        }\n        /* Dark: muted deep-green hover (no bright lime) */\n        .dark .month-tile:hover {\n          background-color: hsl(var(--primary-pressed-dark)) !important;\n        }\n        \n        .month-tile.range-start {\n          background-color: #334238 !important;\n          color: white !important;\n          font-weight: 500;\n          border-radius: 6px !important;\n        }\n        /* Dark: deep green endpoints + light text \u2014 darker than lime primary */\n        .dark .month-tile.range-start {\n          background-color: hsl(var(--primary-hover-dark)) !important;\n          color: hsl(var(--foreground)) !important;\n        }\n        \n        .month-tile.range-end {\n          background-color: #334238 !important;\n          color: white !important;\n          font-weight: 500;\n          border-radius: 6px !important;\n        }\n        .dark .month-tile.range-end {\n          background-color: hsl(var(--primary-hover-dark)) !important;\n          color: hsl(var(--foreground)) !important;\n        }\n        \n        .month-tile.range-middle {\n          background-color: #f4ffc6 !important;\n          color: #2D3748 !important;\n          font-weight: 400;\n        }\n        /* Dark: same green family, slightly deeper for in-range months */\n        .dark .month-tile.range-middle {\n          background-color: hsl(var(--primary-pressed-dark)) !important;\n          color: hsl(var(--foreground)) !important;\n        }\n        \n        .month-tile.range-hover {\n          background-color: #e2e8f0 !important;\n          color: #2D3748 !important;\n          font-weight: 400;\n        }\n        .dark .month-tile.range-hover {\n          background-color: hsl(var(--primary-pressed-dark)) !important;\n          color: hsl(var(--foreground)) !important;\n        }\n        \n        .month-tile:not(.range-start):not(.range-end):not(.range-middle):not(.range-hover):hover {\n          background-color: #e2e8f0;\n        }\n        .dark .month-tile:not(.range-start):not(.range-end):not(.range-middle):not(.range-hover):hover {\n          background-color: hsl(var(--primary-pressed-dark));\n        }\n        \n        .action-buttons {\n          display: flex;\n          justify-content: space-between;\n          margin-top: 0.75rem;\n          padding-top: 0.5rem;\n          border-top: 1px solid #e5e5e5;\n        }\n        .dark .action-buttons {\n          border-top: 1px solid hsl(var(--border));\n        }\n        \n        .instructions {\n          margin-bottom: 0.75rem;\n          text-align: center;\n        }\n        \n        .instructions p {\n          font-size: 0.875rem;\n          color: #666666;\n          margin: 0;\n        }\n        .dark .instructions p {\n          color: hsl(var(--muted-foreground));\n        }\n        .month-tile.disabled {\n          color: #cccccc !important;\n          cursor: not-allowed !important;\n          opacity: 1;\n          background: none !important;\n          pointer-events: none;\n        }\n        .dark .month-tile.disabled {\n          color: hsl(var(--muted-foreground)) !important;\n        }\n        \n        .month-tile.disabled:hover {\n          background: none !important;\n        }\n      " }), _jsxs(Popover, { open: open, onOpenChange: handleOpenChange, children: [_jsx(PopoverTrigger, { asChild: true, children: _jsxs(Button, { variant: "outline", role: "combobox", "aria-expanded": open, disabled: disabled, className: cn('w-[240px] h-9 justify-start text-left font-normal', !tempRange.start && 'text-muted-foreground', disabled && 'cursor-not-allowed opacity-50', className), children: [_jsx(CalendarIcon, { className: "mr-2 h-4 w-4 shrink-0 " }), _jsx("span", { className: "truncate flex-1", children: formatMonthRange() })] }) }), _jsx(PopoverContent
                    // PopoverContent forces !bg-popover after className — use inline bg so dark mode
                    // matches page --background (neutral dark) instead of olive --popover tint.
                    , { 
                        // PopoverContent forces !bg-popover after className — use inline bg so dark mode
                        // matches page --background (neutral dark) instead of olive --popover tint.
                        className: "w-auto min-w-[440px] p-0 bg-white dark:!bg-background shadow-xl border border-gray-200 dark:border-border rounded-xl", style: { backgroundColor: 'hsl(var(--background))' }, align: "start", sideOffset: 8, children: _jsxs("div", { className: "p-4", children: [_jsx("div", { className: "flex flex-row justify-between items-end mb-2", children: _jsxs("div", { className: "flex flex-row justify-between items-end w-full", children: [_jsxs("div", { className: "flex flex-col items-start", children: [_jsx("label", { htmlFor: "month-picker-start", className: "text-xs text-[hsl(var(--primary-pressed-dark))] dark:text-muted-foreground mb-1", children: "Start Month" }), _jsx("input", { id: "month-picker-start", type: "text", value: startInput, onChange: handleStartInputChange, placeholder: "Start month", className: "border rounded px-2 py-1 text-xs border-[hsl(var(--primary-pressed-dark))] focus:ring-1 focus:ring-[hsl(var(--primary-pressed-dark))] outline-none text-left w-24 bg-background text-foreground" })] }), _jsx("span", { className: "text-gray-600 dark:text-muted-foreground text-sm mb-1", children: "\u2014" }), _jsxs("div", { className: "flex flex-col items-start", children: [_jsx("label", { htmlFor: "month-picker-end", className: "text-xs text-gray-500 dark:text-muted-foreground mb-1", children: "End Month" }), _jsx("input", { id: "month-picker-end", type: "text", value: endInput, onChange: handleEndInputChange, placeholder: "End month", className: "border rounded px-2 py-1 text-xs border-[hsl(var(--primary-pressed-dark))] focus:border-[hsl(var(--primary-pressed-dark))] focus:ring-1 focus:ring-[hsl(var(--primary-pressed-dark))] outline-none text-left w-24 bg-background text-foreground" })] })] }) }), _jsx("div", { className: "month-picker-container", children: _jsxs("div", { className: "month-picker-content items-center justify-center", children: [renderYear(startYear, true), renderYear(endYear, false)] }) }), _jsxs("div", { className: "action-buttons", children: [_jsx(Button, { variant: "outline", onClick: function () {
                                                var clearedRange = { start: null, end: null };
                                                setTempRange(clearedRange);
                                                setSelectedRange(clearedRange);
                                                setSelectingEnd(false);
                                                setHoverMonth(null);
                                                setStartInput('');
                                                setEndInput('');
                                            }, children: "Clear" }), _jsxs("div", { className: "flex space-x-2", children: [_jsx(Button, { variant: "outline", onClick: handleCancel, children: "Cancel" }), _jsx(Button, { variant: "default", onClick: handleApply, disabled: !tempRange.start || !tempRange.end, children: "Apply" })] })] })] }) })] })] }));
}
